import { useCallback, useEffect, useRef, useState } from "react";
import {
  CHUNK_ROTATE_MS,
  extensionForMime,
  pickRecorderMimeType,
} from "../../utils/meetingRecorder";

export function useMeetingRecorder({ onChunk, onError }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPausedForMedia, setIsPausedForMedia] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pendingUploads, setPendingUploads] = useState(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const partsRef = useRef([]);
  const mimeRef = useRef("");
  const seqRef = useRef(0);
  const shouldContinueRef = useRef(false);
  const pausedForMediaRef = useRef(false);
  const chunkStartedAtRef = useRef(0);
  const sessionStartedAtRef = useRef(0);
  const rotateTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const stopResolverRef = useRef(null);
  const pauseResolverRef = useRef(null);
  const onChunkRef = useRef(onChunk);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onChunkRef.current = onChunk;
    onErrorRef.current = onError;
  }, [onChunk, onError]);

  const clearRotateTimer = () => {
    if (rotateTimerRef.current) {
      clearTimeout(rotateTimerRef.current);
      rotateTimerRef.current = null;
    }
  };

  const clearTimers = () => {
    clearRotateTimer();
    if (tickTimerRef.current) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  };

  const releaseWakeLock = async () => {
    try {
      await wakeLockRef.current?.release?.();
    } catch {
      /* ignore */
    }
    wakeLockRef.current = null;
  };

  const requestWakeLock = async () => {
    try {
      if (navigator.wakeLock?.request) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch {
      /* iOS / permisos: el aviso de pantalla encendida cubre esto */
    }
  };

  const stopTracks = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const flushBlob = useCallback(async (blob, durationMs, seq) => {
    if (!blob || blob.size < 64) return;
    const mime = blob.type || mimeRef.current || "audio/webm";
    const ext = extensionForMime(mime);
    const file = new File([blob], `chunk-${seq}.${ext}`, { type: mime });
    setPendingUploads((n) => n + 1);
    try {
      await onChunkRef.current?.({ file, seq, durationMs, mimeType: mime });
    } catch (err) {
      onErrorRef.current?.(err);
    } finally {
      setPendingUploads((n) => Math.max(0, n - 1));
    }
  }, []);

  const startChunk = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || !shouldContinueRef.current) return;
    const mime = mimeRef.current;
    const recorder = mime
      ? new MediaRecorder(stream, { mimeType: mime })
      : new MediaRecorder(stream);
    mimeRef.current = recorder.mimeType || mime;
    partsRef.current = [];
    chunkStartedAtRef.current = Date.now();
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) partsRef.current.push(event.data);
    };
    recorder.onerror = (event) => {
      onErrorRef.current?.(event.error || new Error("Error de grabación"));
    };
    recorder.onstop = async () => {
      const blob = new Blob(partsRef.current, {
        type: mimeRef.current || "audio/webm",
      });
      const durationMs = Date.now() - chunkStartedAtRef.current;
      const seq = seqRef.current;
      seqRef.current += 1;
      const upload = flushBlob(blob, durationMs, seq);
      if (shouldContinueRef.current) {
        startChunk();
        await upload;
        return;
      }
      // iPhone: soltar el mic para poder grabar vídeo con la cámara.
      // Liberar la pausa YA: no esperar a que suba el chunk (si la red va lenta
      // el selector de vídeo no abría y parecía «atascado»).
      stopTracks();
      if (pausedForMediaRef.current) {
        const resolvePause = pauseResolverRef.current;
        pauseResolverRef.current = null;
        resolvePause?.();
        await upload;
        return;
      }
      await upload;
      await releaseWakeLock();
      setIsRecording(false);
      const resolveStop = stopResolverRef.current;
      stopResolverRef.current = null;
      resolveStop?.();
    };
    recorderRef.current = recorder;
    recorder.start();
    rotateTimerRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
    }, CHUNK_ROTATE_MS);
  }, [flushBlob]);

  const stop = useCallback(async () => {
    pausedForMediaRef.current = false;
    setIsPausedForMedia(false);
    pauseResolverRef.current = null;
    shouldContinueRef.current = false;
    clearTimers();
    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 8000);
        stopResolverRef.current = () => {
          clearTimeout(timeout);
          resolve();
        };
        try {
          recorder.stop();
        } catch {
          clearTimeout(timeout);
          resolve();
        }
      });
      return;
    }
    stopTracks();
    await releaseWakeLock();
    setIsRecording(false);
  }, []);

  /**
   * Libera el micrófono sin cerrar la sesión de reunión.
   * Necesario en iOS: no deja grabar vídeo con la cámara si el mic sigue cogido.
   */
  const pauseForMediaCapture = useCallback(async () => {
    if (!isRecording && !streamRef.current && !recorderRef.current) return;
    if (pausedForMediaRef.current && !recorderRef.current) return;

    pausedForMediaRef.current = true;
    setIsPausedForMedia(true);
    shouldContinueRef.current = false;
    clearRotateTimer();

    const recorder = recorderRef.current;
    recorderRef.current = null;
    if (recorder && recorder.state !== "inactive") {
      await new Promise((resolve) => {
        const timeout = setTimeout(resolve, 8000);
        pauseResolverRef.current = () => {
          clearTimeout(timeout);
          resolve();
        };
        try {
          recorder.stop();
        } catch {
          clearTimeout(timeout);
          stopTracks();
          resolve();
        }
      });
      return;
    }
    stopTracks();
  }, [isRecording]);

  const resumeAfterMediaCapture = useCallback(async () => {
    if (!pausedForMediaRef.current && !isPausedForMedia) return;
    if (typeof MediaRecorder === "undefined") {
      throw new Error("Este teléfono no permite grabar audio en el navegador");
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    mimeRef.current = pickRecorderMimeType() || mimeRef.current;
    shouldContinueRef.current = true;
    pausedForMediaRef.current = false;
    setIsPausedForMedia(false);
    setIsRecording(true);
    await requestWakeLock();
    if (!tickTimerRef.current) {
      tickTimerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - sessionStartedAtRef.current);
      }, 500);
    }
    startChunk();
  }, [isPausedForMedia, startChunk]);

  const start = useCallback(
    async (nextSeq = 0) => {
      if (typeof MediaRecorder === "undefined") {
        throw new Error(
          "Este teléfono no permite grabar audio en el navegador",
        );
      }
      pausedForMediaRef.current = false;
      setIsPausedForMedia(false);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mimeRef.current = pickRecorderMimeType();
      seqRef.current = Number.isFinite(Number(nextSeq)) ? Number(nextSeq) : 0;
      shouldContinueRef.current = true;
      sessionStartedAtRef.current = Date.now();
      setElapsedMs(0);
      setIsRecording(true);
      await requestWakeLock();
      tickTimerRef.current = setInterval(() => {
        setElapsedMs(Date.now() - sessionStartedAtRef.current);
      }, 500);
      startChunk();
    },
    [startChunk],
  );

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible" && isRecording) {
        requestWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isRecording]);

  useEffect(
    () => () => {
      shouldContinueRef.current = false;
      pausedForMediaRef.current = false;
      clearTimers();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.stop();
        } catch {
          /* unmount */
        }
      }
      stopTracks();
      releaseWakeLock();
    },
    [],
  );

  return {
    isRecording,
    isPausedForMedia,
    elapsedMs,
    pendingUploads,
    start,
    stop,
    pauseForMediaCapture,
    resumeAfterMediaCapture,
  };
}
